import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { DailyCheckIn } from '../components/credits/DailyCheckIn';
import { ensureUserCredits } from '../lib/credits';
import { getUtcToday } from '../lib/local-date';
import { isAgentPlan } from '../lib/subscriptions';
import { useAuth } from './AuthContext';
import { useTravellerType } from '../hooks/useTravellerType';
import { useOnboarding } from './OnboardingContext';
import { useSubscription } from './SubscriptionContext';

interface DailyCheckInCtx {
  showCheckInIfNeeded: () => void;
  hasCheckedInToday: boolean;
  refreshCheckInStatus: () => Promise<void>;
}

const Ctx = createContext<DailyCheckInCtx>({ showCheckInIfNeeded: () => {}, hasCheckedInToday: false, refreshCheckInStatus: async () => {} });

export function DailyCheckInProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { isCasual } = useTravellerType();
  const { subscription } = useSubscription();
  const { state: onboardingState } = useOnboarding();
  const onboardingComplete = onboardingState.phase === 'complete';
  // Agents (agent-50/200/unlimited) never see gamification — they buy curated
  // content with money instead of earning credits via daily check-in / streaks.
  const isAgent = isAgentPlan(subscription.planSlug);
  const gamificationDisabled = isCasual || isAgent;
  const today = getUtcToday();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem('tg_checkin_dismissed_date') === today; } catch { return false; }
  });
  const [credits, setCredits] = useState<{ daily_streak: number; last_check_in: string | null } | null>(null);
  const [initialLoaded, setInitialLoaded] = useState(false);

  const refreshCheckInStatus = useCallback(async () => {
    if (!user) return;
    try {
      const data = await ensureUserCredits(user.id);
      setCredits({ daily_streak: data.daily_streak, last_check_in: data.last_check_in });
    } catch {}
  }, [user]);

  // Load credit status whenever user becomes available
  useEffect(() => {
    if (!user || gamificationDisabled) {
      setCredits(null);
      setInitialLoaded(false);
      return;
    }
    const timer = setTimeout(async () => {
      await refreshCheckInStatus();
      setInitialLoaded(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, [user, gamificationDisabled, refreshCheckInStatus]);

  // Show modal after initial load if not checked in today.
  // Gating:
  // - Only Enthusiasts get the daily check-in (Casuals and Agents don't use
  //   gamification — Agents buy curated content with money instead).
  // - Wait for onboarding to finish so the modal does not flash on top of the
  //   welcome wizard / spotlight tour for first-time users.
  // - `dismissed` is keyed by UTC day, so once the user closes today's popup
  //   it does not reappear on refresh or page change (the navbar credits
  //   counter keeps pulsing instead).
  useEffect(() => {
    if (gamificationDisabled) return;
    if (!onboardingComplete) return;
    if (initialLoaded && credits && credits.last_check_in !== today && !dismissed) {
      // Don't interrupt if user is filling a form
      const activeEl = document.activeElement;
      if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.tagName === 'SELECT')) {
        return;
      }
      setShow(true);
    }
  }, [initialLoaded, credits, today, dismissed, gamificationDisabled, onboardingComplete]);

  const showCheckInIfNeeded = useCallback(() => {
    if (gamificationDisabled || !user) return;
    // Manual trigger (e.g., navbar credits banner click): bypass the auto-popup
    // suppression flags so the modal opens even if the user dismissed today's
    // automatic popup.
    setDismissed(false);
    try { localStorage.removeItem('tg_checkin_dismissed_date'); } catch {}
    refreshCheckInStatus();
    setShow(true);
  }, [user, gamificationDisabled, refreshCheckInStatus]);

  // Don't pulse until we know the actual status
  const hasCheckedInToday = credits === null ? true : credits.last_check_in === today;

  const handleComplete = useCallback(async () => {
    await refreshCheckInStatus();
  }, [refreshCheckInStatus]);

  return (
    <Ctx.Provider value={{ showCheckInIfNeeded, hasCheckedInToday, refreshCheckInStatus }}>
      {children}
      {show && credits && user && !hasCheckedInToday && !dismissed && !gamificationDisabled && (
        <DailyCheckIn
          userId={user.id}
          currentStreak={credits.daily_streak}
          lastCheckIn={credits.last_check_in}
          onCheckInComplete={handleComplete}
          onClose={() => { setShow(false); setDismissed(true); try { localStorage.setItem('tg_checkin_dismissed_date', today); } catch {} }}
        />
      )}
    </Ctx.Provider>
  );
}

export const useDailyCheckIn = () => useContext(Ctx);
